import React from "react";
import { redirect, useLoaderData } from "react-router";
import { Link } from "react-router-dom";

import classNames from "classnames";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { SubmitHandler, useForm } from "react-hook-form";
import invariant from "tiny-invariant";

import Ratings from "./components/Ratings";
import { CoachReviewProvider } from "./contexts/CoachReview";
import coachReviewSchema from "./lib/coachReviewSchema";
import { CoachReviewFormData } from "./types/coachreview";

import { store } from "../../..";
import "../../../assets/css/umphub/pages/coachreview.scss";
import { useAppDispatch, useAppSelector } from "../../../hooks/reduxHooks";
import { getGame, getGameRatings, updateTeamRatings } from "../../../lib/api";
import { IGame, ITeamRating } from "../../../types/api";
import { selectUser } from "../../../features/auth/authSlice";
import { alertActions } from "../../../features/alert/alertSlice";

export const loader = async ({ params }: any) => {
    const { gameId } = params;
    const user = selectUser(store.getState());
    invariant(user);

    const [{ game }, { ratings }] = await Promise.all([
        getGame(gameId),
        getGameRatings(parseInt(gameId), "team"),
    ]);

    if (!game.umpires?.some(u => u.id === user.id)) {
        return redirect("/my-schedule/past");
    }

    return {
        game,
        ratings,
    };
};

const CoachReview = (): React.JSX.Element => {
    const { game, ratings } = useLoaderData() as {
        game: IGame;
        ratings: ITeamRating[];
    };

    const dispatch = useAppDispatch();
    const user = useAppSelector(selectUser);
    invariant(user);

    const homeTeamRatings = ratings.find(r => r.team.id === game.home.id);
    const visitorTeamRatings = ratings.find(r => r.team.id == game.visitor.id);

    const methods = useForm<CoachReviewFormData>({
        mode: "onSubmit",
        resolver: yupResolver(coachReviewSchema),
        defaultValues: {
            home: {
                headCoach: homeTeamRatings?.ratings.headCoach || 0,
                asstCoach: homeTeamRatings?.ratings.asstCoach || 0,
                spectators: homeTeamRatings?.ratings.spectators || 0,
                overall: homeTeamRatings?.ratings.overall || 0,
            },
            visitor: {
                headCoach: visitorTeamRatings?.ratings.headCoach || 0,
                asstCoach: visitorTeamRatings?.ratings.asstCoach || 0,
                spectators: visitorTeamRatings?.ratings.spectators || 0,
                overall: visitorTeamRatings?.ratings.overall || 0,
            },
        },
    });

    const onSubmit: SubmitHandler<
        Partial<CoachReviewFormData>
    > = async data => {
        await updateTeamRatings(data, game.id, user.id)
            .then(() => {
                dispatch(
                    alertActions.success({
                        title: "Coach review submitted successfully",
                        dismissible: true,
                    }),
                );

                methods.reset(undefined, { keepValues: true });
            })
            .catch(e => {
                dispatch(
                    alertActions.error({
                        title: "The server encountered a problem submitting the review.",
                        text: "If the problem continues, please reach out to an admin.",
                        dismissible: true,
                    }),
                );
            });
    };

    return (
        <CoachReviewProvider
            ratings={ratings}
            game={game}
            formMethods={methods}
        >
            <Container className={classNames("mt-4", "mb-5")}>
                <Link to="/my-schedule/past">Back to games list</Link>
                <Form
                    onSubmit={methods.handleSubmit(onSubmit)}
                    id="coachReviewForm"
                >
                    <Row>
                        <Col lg={6} className={classNames("teamRating")}>
                            <div>
                                <div className="bg-body-primary heading">
                                    * To rate a category, please click on the
                                    appropriate star rating
                                </div>
                                <div className="bg-body-secondary teamInfo">
                                    <div className={`team visitor`}>
                                        <p>
                                            <b>Visiting Team:</b>{" "}
                                            {game.visitor.name}
                                        </p>
                                        <p>
                                            <b>Head Coach:</b>{" "}
                                            {`${game.visitor.coach.firstName} ${game.visitor.coach.lastName}`}
                                        </p>
                                        <Ratings
                                            team="visitor"
                                            ratedTeam={visitorTeamRatings}
                                        />
                                    </div>
                                </div>
                            </div>
                        </Col>
                        <Col lg={6} className={classNames("teamRating")}>
                            <div>
                                <div className="bg-body-primary heading">
                                    * To rate a category, please click on the
                                    appropriate star rating
                                </div>
                                <div className="bg-body-secondary teamInfo">
                                    <div className={`team home`}>
                                        <p>
                                            <b>Home Team:</b> {game.home.name}
                                        </p>
                                        <p>
                                            <b>Head Coach:</b>{" "}
                                            {`${game.home.coach.firstName} ${game.home.coach.lastName}`}
                                        </p>
                                        <Ratings
                                            team="home"
                                            ratedTeam={homeTeamRatings}
                                        />
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Button type="submit">Submit Ratings</Button>
                        </Col>
                    </Row>
                </Form>
            </Container>
        </CoachReviewProvider>
    );
};

export default CoachReview;
